import cornerstone from 'cornerstone-core';

const nextFrame = () =>
  new Promise(resolve => requestAnimationFrame(() => resolve()));

/**
 * Renders every image of a stack to an offscreen canvas and returns them as
 * JPEG data URLs.
 *
 * A single canvas is reused for the whole stack so that a 500 slice CT stays
 * feasible, and a frame is yielded between images so the UI keeps responding
 * while the sheets are being prepared.
 *
 * @param {Object} options
 * @param {string[]} options.imageIds
 * @param {Object} [options.viewport] VOI/invert inherited from the on screen viewport
 * @param {number} [options.size=512] square canvas side, in pixels
 * @param {Function} [options.onProgress] (done, total) => void
 * @returns {Promise<string[]>} data URLs, one per image
 */
export default async function captureDisplaySetImages({
  imageIds,
  viewport,
  size = 512,
  onProgress,
}) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const images = [];

  for (let index = 0; index < imageIds.length; index++) {
    const image = await cornerstone.loadAndCacheImage(imageIds[index]);

    // renderToCanvas starts from the image's default viewport and merges
    // whatever is passed in, so only the VOI/invert the user set on screen
    // needs to be inherited.
    cornerstone.renderToCanvas(canvas, image, viewport);
    images.push(canvas.toDataURL('image/jpeg', 0.9));

    if (onProgress) {
      onProgress(index + 1, imageIds.length);
    }

    await nextFrame();
  }

  return images;
}
